import { useEffect, useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { exportToExcel } from "../utils/exportData";

type Donation = {
  id: string; first_name: string; last_name: string; phone: string | null; email: string;
  amount: number; project_observatoire: boolean; project_exprime: boolean;
  donation_type: string; payment_status: string; created_at: string;
};

const eur = (n: number) => new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", maximumFractionDigits: 2 }).format(n);
const typeLabel = (t: string) => (t === "promesse" ? "Promesse" : "Paiement immédiat");
const statusLabel = (s: string) => (s === "paid" ? "Payé" : s === "promise" ? "À rappeler" : "Paiement non finalisé");
const projects = (d: Donation) => [d.project_observatoire && "Observatoire", d.project_exprime && "Exprime la charte"].filter(Boolean).join(" + ");

export const DonationsTab = () => {
  const [rows, setRows] = useState<Donation[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (supabase.from("donations" as never) as any).select("*").order("created_at", { ascending: false })
      .then(({ data }: { data: Donation[] | null }) => { setRows(data ?? []); setLoading(false); });
  }, []);

  const t = useMemo(() => {
    const valid = rows.filter((r) => r.donation_type === "promesse" || r.payment_status === "paid");
    const sum = (f: (r: Donation) => boolean) => valid.filter(f).reduce((a, r) => a + r.amount, 0);
    return {
      total: sum(() => true),
      promesses: sum((r) => r.donation_type === "promesse"),
      paid: sum((r) => r.donation_type !== "promesse"),
      obs: valid.reduce((a, r) => a + (r.project_observatoire ? (r.project_exprime ? r.amount / 2 : r.amount) : 0), 0),
      exp: valid.reduce((a, r) => a + (r.project_exprime ? (r.project_observatoire ? r.amount / 2 : r.amount) : 0), 0),
    };
  }, [rows]);

  const exportXls = () => exportToExcel(rows.map((r) => ({
    NOM: r.last_name, PRENOM: r.first_name, EMAIL: r.email, TELEPHONE: r.phone ?? "",
    MONTANT: r.amount, PROJETS: projects(r),
    "PART OBSERVATOIRE": r.project_observatoire ? (r.project_exprime ? r.amount / 2 : r.amount) : 0,
    "PART EXPRIME LA CHARTE": r.project_exprime ? (r.project_observatoire ? r.amount / 2 : r.amount) : 0, TYPE: typeLabel(r.donation_type), ETAT: statusLabel(r.payment_status),
    DATE: new Date(r.created_at).toLocaleString("fr-FR"),
  })), `Appel_au_don_${new Date().toISOString().slice(0, 10)}`);

  return (
    <div className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-3">
        <Card className="bg-[#00123A] text-white sm:col-span-3">
          <CardContent className="py-6 text-center">
            <div className="text-sm uppercase tracking-wide opacity-80">Montant total des dons et promesses</div>
            <div className="text-5xl font-bold my-2">{eur(t.total)}</div>
            <div className="text-sm opacity-80">dont promesses : {eur(t.promesses)} — dont payés : {eur(t.paid)}</div>
          </CardContent>
        </Card>
        <Card className="sm:col-span-3 sm:grid sm:grid-cols-2">
          <CardContent className="py-6 text-center">
            <div className="text-sm text-muted-foreground">Projet Observatoire</div>
            <div className="text-4xl font-bold text-[#00123A]">{eur(t.obs)}</div>
          </CardContent>
          <CardContent className="py-6 text-center">
            <div className="text-sm text-muted-foreground">Projet Exprime la charte</div>
            <div className="text-4xl font-bold text-[#00123A]">{eur(t.exp)}</div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-4 flex-wrap">
          <CardTitle className="text-base">Détail des dons ({rows.length})</CardTitle>
          <div className="flex gap-2 flex-wrap">
            <Button variant="outline" asChild><a href="/qr-don-AHV2026.png" download>Télécharger le QR code</a></Button>
            <Button variant="outline" onClick={exportXls} disabled={!rows.length}>Exporter Excel</Button>
          </div>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          {loading ? <p className="text-center py-6 text-muted-foreground">Chargement…</p> : (
            <table className="w-full min-w-[900px] text-sm">
              <thead><tr className="border-b text-left">
                {["Date", "Nom", "Prénom", "Email", "Téléphone", "Montant", "Projet(s)", "Type", "État"].map((h) => <th key={h} className="py-2 pr-3">{h}</th>)}
              </tr></thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className="border-b">
                    <td className="py-2 pr-3">{new Date(r.created_at).toLocaleDateString("fr-FR")}</td>
                    <td className="pr-3">{r.last_name}</td><td className="pr-3">{r.first_name}</td>
                    <td className="pr-3">{r.email}</td><td className="pr-3">{r.phone}</td>
                    <td className="pr-3 font-semibold">{eur(r.amount)}</td>
                    <td className="pr-3">{projects(r)}</td><td className="pr-3">{typeLabel(r.donation_type)}</td>
                    <td className="pr-3">{statusLabel(r.payment_status)}</td>
                  </tr>
                ))}
                {!rows.length && <tr><td colSpan={9} className="text-center py-6 text-muted-foreground">Aucun don pour le moment</td></tr>}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
